import React, { useRef, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled, ThemeProvider, createTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import { NavLink } from "react-router-dom";
import { GoogleIcon } from './CustomIcons';
import { useAuth } from '../../hooks/useAuth';
import { useUser } from '../../components/UserContext';
import { useNavigate } from 'react-router-dom';
import logger from '../../utils/logger';
import {
  AUTH_ERROR_CODES,
  LINK_INVITE_AUTH_ERROR_MESSAGE,
} from '../../services/authService';

const theme = createTheme({
  palette: {
    mode: "light",
  },
});

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(1),
  margin: "auto",
  maxWidth: "450px",
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  padding: 20,
  marginBottom: "10vh",
  position: "relative",
}));

export default function SignIn() {
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [isGoogleAuthProcessing, setIsGoogleAuthProcessing] = useState(false);
  const emailRef = useRef();
  const navigate = useNavigate();
  
  const { 
    signInWithMagic, 
    redirectToGoogle, 
    loading, 
    error, 
    clearError 
  } = useAuth();
  
  const { loading: userLoading, user, authError } = useUser();

  // Check for Google OAuth code in URL and set processing state
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');
    
    if (code) {
      setIsGoogleAuthProcessing(true);
    }
    
    // Handle OAuth errors
    if (error) {
      setEmailError(true);
      // Use the specific error message if it's not the generic oauth_failed
      if (error === 'oauth_failed') {
        setEmailErrorMessage('Google authentication failed. Please try again.');
      } else {
        setEmailErrorMessage(error);
      }
      // Clear the error from URL
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('error');
      window.history.replaceState({}, "", newUrl.toString());
    }
  }, []);

  // Clear auth error when user state changes (e.g., after successful login)
  useEffect(() => {
    if (user && !userLoading) {
      clearAuthError();
    }
  }, [user, userLoading]);

  // Handle error state from hook
  useEffect(() => {
    if (error) {
      setEmailError(true);
      setEmailErrorMessage(error);
      clearError();
    }
  }, [error, clearError]);

  useEffect(() => {
    if (authError && !userLoading && authError !== AUTH_ERROR_CODES.LINK_INVITE_REVOKED) {
      setEmailError(true);
      setEmailErrorMessage(authError);
    }
  }, [authError, userLoading]);

  // Clear auth error when user starts typing
  const clearAuthError = () => {
    setEmailError(false);
    setEmailErrorMessage("");
  };

  // Reset Google auth processing state when user loading is complete
  useEffect(() => {
    if (!userLoading && isGoogleAuthProcessing) {
      setIsGoogleAuthProcessing(false);
    }
  }, [userLoading, isGoogleAuthProcessing]);

  // Redirect authenticated users to the main app
  useEffect(() => {
    if (user && !userLoading) {
      // Check for stored redirect path from OAuth flow
      const storedRedirectPath = sessionStorage.getItem('authRedirectPath');
      if (storedRedirectPath) sessionStorage.removeItem('authRedirectPath');
      // A link session must not be sent back to staff-only /snap.
      const destination = storedRedirectPath && !(user.isLinkAuthenticated && storedRedirectPath === '/snap')
        ? storedRedirectPath
        : '/analysis';
      if (storedRedirectPath) {
        logger.log('🔐 SignIn: Found stored redirect path, navigating to:', destination);
      }
      navigate(destination, { replace: true });
    }
  }, [user, userLoading, navigate]);

  if (!userLoading && !user && authError === AUTH_ERROR_CODES.LINK_INVITE_REVOKED) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SignInContainer
          direction="column"
          justifyContent="space-between"
          className="flex flex-col w-full h-full items-center"
        >
          <Card variant="outlined">
            <Typography variant="h4" className="text-center">
              Failed Sign In
            </Typography>
            <Typography
              variant="body1"
              component="p"
              align="center"
              color="text.primary"
              sx={{ mt: 2, lineHeight: 1.6 }}
            >
              {LINK_INVITE_AUTH_ERROR_MESSAGE}
            </Typography>
          </Card>
        </SignInContainer>
      </ThemeProvider>
    );
  }

  // Show loading while checking authentication status
  if (userLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SignInContainer direction="column" justifyContent="space-between" className="flex flex-col w-full h-full items-center">
          <Card variant="outlined">
            <Typography variant="h4" className="text-center">
              Checking authentication...
              <CircularProgress />
            </Typography>
          </Card>
        </SignInContainer>
      </ThemeProvider>
    );
  }

  // If user is already authenticated, show loading while redirecting
  if (user && !userLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SignInContainer direction="column" justifyContent="space-between" className="flex flex-col w-full h-full items-center">
          <Card variant="outlined">
            <Typography variant="h4" className="text-center">
              Redirecting to app...
              <CircularProgress />
            </Typography>
          </Card>
        </SignInContainer>
      </ThemeProvider>
    );
  }

  const handleMagicLinkSubmit = async (event) => {
    event.preventDefault();
    const email = emailRef.current.value;

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailError(true);
      setEmailErrorMessage("Please enter a valid email address.");
      return;
    }

    clearAuthError();

    try {
      await signInWithMagic(email);
    } catch (error) {
      // Error handling is done in the hook and useEffect above
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SignInContainer direction="column" justifyContent="space-between" className="flex flex-col w-full h-full items-center">
        <Card variant="outlined">
          {loading || userLoading || isGoogleAuthProcessing ? (
            <Typography variant="h4" className="text-center">
              Signing in...
              <CircularProgress />
            </Typography>
          ) : (
            <>
              <Typography variant="h4" className="text-center">
                Sign in
              </Typography>
              <Typography variant="body2" className="text-center">
                Welcome, please sign in to continue
              </Typography>
              <Box
                component="form"
                onSubmit={handleMagicLinkSubmit}
                noValidate
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <TextField
                    error={emailError}
                    helperText={emailErrorMessage}
                    id="email"
                    type="email"
                    onChange={clearAuthError}
                    placeholder="your@email.com"
                    required
                    fullWidth
                    inputRef={emailRef}
                  />
                </FormControl>
                <Button type="submit" variant="contained">
                  Sign in with Email
                </Button>
              </Box>
              <Divider sx={{ mt: 1 }}>OR</Divider>
              <Button
                type="submit"
                fullWidth
                variant="outlined"
                onClick={redirectToGoogle}
                startIcon={<GoogleIcon />}
              >
                Sign in with Google
              </Button>

              <NavLink to="/" variant="body2" align="center" className={'text-blue-500 underline'} sx={{ mt: 1 }}>
                Contact sales for access
              </NavLink>
            </>
          )}
        </Card>
      </SignInContainer>
    </ThemeProvider>
  );
}
